export interface RGB {
  r: number;
  g: number;
  b: number;
}

import type { ChromaKeySettings, Effect } from "../types/timeline";
export type { ChromaKeySettings };

/**
 * Canonical meaning of the three ChromaKeySettings numbers. Every renderer that
 * keys a pixel obeys it: the CPU pass in VideoEffectsEngine (the one preview and
 * export actually run), its parked WebGL shader, and ChromaKeyEngine below.
 *
 * Colors are compared as Euclidean distance in unit RGB space, so the largest
 * distance two colors can have is sqrt(3), black to white.
 *
 * tolerance: 0..1, a fraction of MAX_RGB_DISTANCE. Pixels nearer to keyColor
 *   than tolerance * MAX_RGB_DISTANCE key out fully. This is what lets the
 *   inspector show it as a plain percentage, since 100% keys the whole frame.
 * edgeSoftness: 0..1, also a fraction of MAX_RGB_DISTANCE, giving the half
 *   width of the smoothstep ramp centred on the tolerance threshold.
 * spillSuppression: 0..1, how much of the key color's excess is pulled out of
 *   partially keyed pixels. Fully opaque and fully keyed pixels are untouched,
 *   so it only cleans the fringe.
 */
export const MAX_RGB_DISTANCE = Math.sqrt(3);

function smoothstep(edge0: number, edge1: number, x: number): number {
  // edgeSoftness 0 collapses the ramp to a hard cut, which the usual
  // (x - edge0) / (edge1 - edge0) form turns into NaN exactly at the threshold.
  if (edge1 <= edge0) return x < edge0 ? 0 : 1;
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * Opacity of one pixel, 0 fully keyed out and 1 fully kept. `distance` is in
 * unit RGB space; `tolerance` and `edgeSoftness` are the stored settings.
 */
export function chromaKeyAlpha(
  distance: number,
  tolerance: number,
  edgeSoftness: number,
): number {
  const threshold = tolerance * MAX_RGB_DISTANCE;
  const ramp = edgeSoftness * MAX_RGB_DISTANCE;
  return smoothstep(threshold - ramp, threshold + ramp, distance);
}

/**
 * Pulls the key color's excess out of a partially keyed pixel. Channels are in
 * unit RGB space.
 */
export function suppressChromaSpill(
  color: RGB,
  keyColor: RGB,
  amount: number,
  alpha: number,
): RGB {
  if (amount <= 0 || alpha >= 1 || alpha <= 0) {
    return color;
  }
  const spillFactor = 1 - alpha;
  const maxKey = Math.max(keyColor.r, keyColor.g, keyColor.b);
  let { r, g, b } = color;

  if (keyColor.g === maxKey) {
    g -= Math.max(0, g - (r + b) / 2) * amount * spillFactor;
  } else if (keyColor.b === maxKey) {
    b -= Math.max(0, b - (r + g) / 2) * amount * spillFactor;
  } else {
    r -= Math.max(0, r - (g + b) / 2) * amount * spillFactor;
  }

  return {
    r: Math.max(0, Math.min(1, r)),
    g: Math.max(0, Math.min(1, g)),
    b: Math.max(0, Math.min(1, b)),
  };
}

/**
 * Prefix of the effect synthesized from Clip.chromaKey. It is derived from the
 * clip id so the entry is stable across frames and can be told apart from the
 * chromaKey effects a user adds from the Effects panel, which live in
 * Clip.effects and must stay listed and removable there.
 */
export const CLIP_CHROMA_KEY_EFFECT_ID_PREFIX = "clip-chroma-key-";

export function isClipChromaKeyEffect(effect: { id: string }): boolean {
  return effect.id.startsWith(CLIP_CHROMA_KEY_EFFECT_ID_PREFIX);
}

export function buildClipChromaKeyEffect(
  clipId: string,
  chromaKey: ChromaKeySettings | undefined,
): Effect | null {
  if (!chromaKey || !chromaKey.enabled) {
    return null;
  }
  return {
    id: `${CLIP_CHROMA_KEY_EFFECT_ID_PREFIX}${clipId}`,
    type: "chromaKey",
    enabled: true,
    params: {
      keyColor: { ...chromaKey.keyColor },
      tolerance: chromaKey.tolerance,
      edgeSoftness: chromaKey.edgeSoftness,
      spillSuppression: chromaKey.spillSuppression,
    },
  };
}

/**
 * Clip.chromaKey is what the Green Screen inspector writes and what save, undo
 * and load carry. Only Effect entries reach a renderer, so the preview bridge
 * and the export renderer both funnel the setting through here rather than each
 * growing a keying pass of its own.
 */
export function withClipChromaKeyEffect(
  clipId: string,
  chromaKey: ChromaKeySettings | undefined,
  effects: Effect[],
): Effect[] {
  const synthesized = buildClipChromaKeyEffect(clipId, chromaKey);
  if (!synthesized) {
    return effects;
  }
  // An enabled Effects-panel "Chroma Key" already keys this clip, and a second
  // pass would key the already keyed result, so the user's own entry wins.
  if (effects.some((effect) => effect.type === "chromaKey" && effect.enabled)) {
    return effects;
  }
  // Keying goes first so grading and stylizing effects run on the matted frame.
  return [synthesized, ...effects];
}

export interface ChromaKeyResult {
  image: ImageBitmap;
  processingTime: number;
  gpuAccelerated: boolean;
}

export interface ChromaKeyMatte {
  matte: ImageData;
  transparentPixels: number;
  totalPixels: number;
}

export interface ChromaKeyEngineConfig {
  width: number;
  height: number;
  useGPU?: boolean;
}

export const DEFAULT_CHROMA_KEY_SETTINGS: ChromaKeySettings = {
  enabled: false,
  keyColor: { r: 0, g: 1, b: 0 }, // Pure green
  tolerance: 0.3,
  edgeSoftness: 0.1,
  spillSuppression: 0.5,
};

export function createDefaultChromaKeySettings(): ChromaKeySettings {
  return { ...DEFAULT_CHROMA_KEY_SETTINGS };
}

export class ChromaKeyEngine {
  private canvas: OffscreenCanvas;
  private ctx: OffscreenCanvasRenderingContext2D;
  private width: number;
  private height: number;
  private clipSettings: Map<string, ChromaKeySettings> = new Map();

  constructor(config: ChromaKeyEngineConfig) {
    this.width = config.width;
    this.height = config.height;
    this.canvas = new OffscreenCanvas(config.width, config.height);
    this.ctx = this.canvas.getContext("2d", { willReadFrequently: true })!;
  }

  // Match the working canvas to the incoming frame so the source is processed
  // at its native resolution/aspect (the subsequent drawImage(...,this.width,
  // this.height) calls become 1:1) instead of being stretched to the fixed
  // engine size. Output is then aspect-correct and fit downstream.
  private resizeTo(width: number, height: number): void {
    if (width <= 0 || height <= 0) return;
    if (this.width === width && this.height === height) return;
    this.width = width;
    this.height = height;
    this.canvas.width = width;
    this.canvas.height = height;
  }

  enableChromaKey(clipId: string): void {
    const existing = this.clipSettings.get(clipId);
    if (existing) {
      this.clipSettings.set(clipId, { ...existing, enabled: true });
    } else {
      this.clipSettings.set(clipId, {
        ...createDefaultChromaKeySettings(),
        enabled: true,
      });
    }
  }

  disableChromaKey(clipId: string): void {
    const existing = this.clipSettings.get(clipId);
    if (existing) {
      this.clipSettings.set(clipId, { ...existing, enabled: false });
    }
  }

  isEnabled(clipId: string): boolean {
    return this.clipSettings.get(clipId)?.enabled ?? false;
  }

  setKeyColor(clipId: string, color: RGB): void {
    const existing =
      this.clipSettings.get(clipId) || createDefaultChromaKeySettings();
    this.clipSettings.set(clipId, {
      ...existing,
      keyColor: {
        r: Math.max(0, Math.min(1, color.r)),
        g: Math.max(0, Math.min(1, color.g)),
        b: Math.max(0, Math.min(1, color.b)),
      },
    });
  }

  sampleKeyColor(image: ImageBitmap, x: number, y: number): RGB {
    this.resizeTo(image.width, image.height);
    // Draw image to canvas
    this.ctx.clearRect(0, 0, this.width, this.height);
    this.ctx.drawImage(image, 0, 0, this.width, this.height);
    const px = Math.floor(x * this.width);
    const py = Math.floor(y * this.height);
    const imageData = this.ctx.getImageData(px, py, 1, 1);

    return {
      r: imageData.data[0] / 255,
      g: imageData.data[1] / 255,
      b: imageData.data[2] / 255,
    };
  }

  setTolerance(clipId: string, tolerance: number): void {
    const existing =
      this.clipSettings.get(clipId) || createDefaultChromaKeySettings();
    this.clipSettings.set(clipId, {
      ...existing,
      tolerance: Math.max(0, Math.min(1, tolerance)),
    });
  }

  setEdgeSoftness(clipId: string, softness: number): void {
    const existing =
      this.clipSettings.get(clipId) || createDefaultChromaKeySettings();
    this.clipSettings.set(clipId, {
      ...existing,
      edgeSoftness: Math.max(0, Math.min(1, softness)),
    });
  }

  setSpillSuppression(clipId: string, amount: number): void {
    const existing =
      this.clipSettings.get(clipId) || createDefaultChromaKeySettings();
    this.clipSettings.set(clipId, {
      ...existing,
      spillSuppression: Math.max(0, Math.min(1, amount)),
    });
  }

  getSettings(clipId: string): ChromaKeySettings | undefined {
    return this.clipSettings.get(clipId);
  }

  setSettings(clipId: string, settings: ChromaKeySettings): void {
    this.clipSettings.set(clipId, { ...settings });
  }

  async applyChromaKey(
    image: ImageBitmap,
    clipId: string,
  ): Promise<ChromaKeyResult> {
    const startTime = performance.now();
    const settings = this.clipSettings.get(clipId);
    if (!settings || !settings.enabled) {
      return {
        image: await createImageBitmap(image),
        processingTime: performance.now() - startTime,
        gpuAccelerated: false,
      };
    }

    return this.applyChromaKeyWithSettings(image, settings, startTime);
  }

  async applyChromaKeyWithSettings(
    image: ImageBitmap,
    settings: ChromaKeySettings,
    startTime: number = performance.now(),
  ): Promise<ChromaKeyResult> {
    this.resizeTo(image.width, image.height);
    // Draw source image
    this.ctx.clearRect(0, 0, this.width, this.height);
    this.ctx.drawImage(image, 0, 0, this.width, this.height);
    const imageData = this.ctx.getImageData(0, 0, this.width, this.height);
    const data = imageData.data;

    const { keyColor, tolerance, edgeSoftness, spillSuppression } = settings;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i] / 255;
      const g = data[i + 1] / 255;
      const b = data[i + 2] / 255;
      const distance = this.colorDistance(r, g, b, keyColor);
      let alpha = this.calculateAlpha(distance, tolerance, edgeSoftness);
      if (spillSuppression > 0 && alpha > 0) {
        const spillResult = this.suppressSpill(
          r,
          g,
          b,
          keyColor,
          spillSuppression,
          alpha,
        );
        data[i] = Math.round(spillResult.r * 255);
        data[i + 1] = Math.round(spillResult.g * 255);
        data[i + 2] = Math.round(spillResult.b * 255);
      }
      data[i + 3] = Math.round(alpha * 255);
    }

    // Put processed data back
    this.ctx.putImageData(imageData, 0, 0);

    const result = await createImageBitmap(this.canvas);

    return {
      image: result,
      processingTime: performance.now() - startTime,
      gpuAccelerated: false,
    };
  }

  getMatte(image: ImageBitmap, clipId: string): ChromaKeyMatte {
    this.resizeTo(image.width, image.height);
    const settings = this.clipSettings.get(clipId);

    if (!settings || !settings.enabled) {
      const matte = this.ctx.createImageData(this.width, this.height);
      for (let i = 0; i < matte.data.length; i += 4) {
        matte.data[i] = 255;
        matte.data[i + 1] = 255;
        matte.data[i + 2] = 255;
        matte.data[i + 3] = 255;
      }
      return {
        matte,
        transparentPixels: 0,
        totalPixels: this.width * this.height,
      };
    }

    // Draw source image
    this.ctx.clearRect(0, 0, this.width, this.height);
    this.ctx.drawImage(image, 0, 0, this.width, this.height);

    const imageData = this.ctx.getImageData(0, 0, this.width, this.height);
    const data = imageData.data;
    const matte = this.ctx.createImageData(this.width, this.height);
    const matteData = matte.data;

    const { keyColor, tolerance, edgeSoftness } = settings;
    let transparentPixels = 0;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i] / 255;
      const g = data[i + 1] / 255;
      const b = data[i + 2] / 255;

      const distance = this.colorDistance(r, g, b, keyColor);
      const alpha = this.calculateAlpha(distance, tolerance, edgeSoftness);
      const alphaValue = Math.round(alpha * 255);
      matteData[i] = alphaValue;
      matteData[i + 1] = alphaValue;
      matteData[i + 2] = alphaValue;
      matteData[i + 3] = 255;

      if (alpha === 0) {
        transparentPixels++;
      }
    }

    return {
      matte,
      transparentPixels,
      totalPixels: this.width * this.height,
    };
  }

  private colorDistance(
    r: number,
    g: number,
    b: number,
    keyColor: RGB,
  ): number {
    const dr = r - keyColor.r;
    const dg = g - keyColor.g;
    const db = b - keyColor.b;
    return Math.sqrt(dr * dr + dg * dg + db * db);
  }

  private calculateAlpha(
    distance: number,
    tolerance: number,
    softness: number,
  ): number {
    return chromaKeyAlpha(distance, tolerance, softness);
  }

  private suppressSpill(
    r: number,
    g: number,
    b: number,
    keyColor: RGB,
    amount: number,
    alpha: number,
  ): RGB {
    return suppressChromaSpill({ r, g, b }, keyColor, amount, alpha);
  }

  async composite(
    foreground: ImageBitmap,
    background: ImageBitmap,
  ): Promise<ImageBitmap> {
    this.resizeTo(foreground.width, foreground.height);
    // Draw background first
    this.ctx.clearRect(0, 0, this.width, this.height);
    this.ctx.drawImage(background, 0, 0, this.width, this.height);

    // Draw foreground on top (alpha channel handles transparency)
    this.ctx.drawImage(foreground, 0, 0, this.width, this.height);

    return createImageBitmap(this.canvas);
  }

  async applyAndComposite(
    foreground: ImageBitmap,
    background: ImageBitmap,
    clipId: string,
  ): Promise<ChromaKeyResult> {
    const startTime = performance.now();
    const keyedResult = await this.applyChromaKey(foreground, clipId);

    // Composite over background
    const composited = await this.composite(keyedResult.image, background);

    // Clean up intermediate result
    keyedResult.image.close();

    return {
      image: composited,
      processingTime: performance.now() - startTime,
      gpuAccelerated: false,
    };
  }

  countTransparentPixels(image: ImageBitmap): number {
    this.resizeTo(image.width, image.height);
    this.ctx.clearRect(0, 0, this.width, this.height);
    this.ctx.drawImage(image, 0, 0, this.width, this.height);

    const imageData = this.ctx.getImageData(0, 0, this.width, this.height);
    const data = imageData.data;

    let count = 0;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] === 0) {
        count++;
      }
    }

    return count;
  }

  resize(width: number, height: number): void {
    this.width = width;
    this.height = height;
    this.canvas.width = width;
    this.canvas.height = height;
  }

  getDimensions(): { width: number; height: number } {
    return { width: this.width, height: this.height };
  }

  clearSettings(clipId: string): void {
    this.clipSettings.delete(clipId);
  }

  clearAllSettings(): void {
    this.clipSettings.clear();
  }
}
